// Lessons: short notes that apply to some kinds of game but not all, selected into the prompt
// by kind. The three tiers for a recurring mistake, in order of preference:
//   1. make it impossible or harmless in the runtime (zero tokens, every game, for ever);
//   2. detect it in the validator with a finding that says the fix (the repair round does the rest);
//   3. say it here, tagged with the kinds it concerns, so the model hears it before writing.
// A lesson that applies to every kind belongs in contract.md instead. Keep each to two lines;
// the worked example in the kind pack is the real teacher.

import type { KindId } from "./spec.js";
import { KINDS } from "./spec.js";

export interface Lesson {
  id: string;
  /** Which kinds hear it. */
  kinds: KindId[];
  text: string;
}

const TOP_DOWN: KindId[] = ["grow", "chase", "stations"];
const GROUNDED: KindId[] = ["side", "dig"];

export const LESSONS: Lesson[] = [
  {
    id: "ground-layer",
    kinds: [...TOP_DOWN, ...GROUNDED],
    text:
      "Anything players walk over or stand on (plots, floor tiles, paths, platforms, blocks) goes on `layer: -1`, whether or not it is static. Within a layer the runtime draws bigger things over smaller ones, so a layer-0 plot wider than a player hides the player. The validator rejects a numerous or static type bigger than the player on layer 0.",
  },
  {
    id: "overlay-layer",
    kinds: [...GROUNDED, "stations"],
    text: "Water, fog, darkness, weather and anything meant to cover players goes on `layer: 1`, with an `r` large enough to stay in every client's view.",
  },
  {
    id: "actor-size",
    kinds: [...TOP_DOWN],
    text: "Give NPCs, enemies and players a similar `r` (12 to 25): the camera's zoom, the culling and the draw order all read it, and a 60-unit chicken dwarfs the player's view.",
  },
  {
    id: "room-density",
    kinds: [...TOP_DOWN],
    text: "Size the world for a full room: about 150,000 to 300,000 square world units per player at `players.max`, so 32 players want roughly 3000×2000 to 4000×2500, and a game that needs contact (tag, eating) sits at the small end.",
  },
  {
    id: "spawn-safety",
    kinds: ["side", "dig"],
    text: "Spawn and respawn players on solid ground above a block, not at a fixed height in the air: a player who starts inside a block or over the void falls for ever.",
  },
];

/** Lessons for a kind, as a markdown list, or an empty string. */
export function lessonsFor(kind: KindId): string {
  if (!KINDS.includes(kind)) return "";
  return LESSONS.filter((l) => l.kinds.includes(kind))
    .map((l) => `- ${l.text}`)
    .join("\n");
}
